import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Chart from "./revenue-chart";
import { InvoicingRevevue } from "../-data/schema";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { useState } from "react";
import { httpClient } from "@/httpClient";
import getPharmacyID from "@/utils/pharmacy_code";
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { RevenueReportResponse } from "@/gen/reports/revenue_pb";
import { PaymentMode } from "@/gen/sales/common_pb";
import { pharmacyClient } from "@/connect";

const API_URL = import.meta.env.VITE_API_URL as string;

const Revenue = ({ data }: { data: RevenueReportResponse }) => {
  const [initialRevenueData, setInitialRevenueData] =
    useState<RevenueReportResponse>(data);

  async function handleDateChange(date: string) {
    const startDate = dayjs(date, "YYYY").startOf("year").unix();
    const endDate = dayjs(date, "YYYY").endOf("year").unix();

    const revenueData = await pharmacyClient.revenueReport({
      startDate: startDate,
      endDate: endDate,
    });

    setInitialRevenueData(revenueData);
  }

  return (
    <div>
      <p className="text-gray-700 text-3xl font-semibold">Revenue</p>
      <div className="flex items-center justify-between mt-6">
        <div className="grid grid-cols-2 gap-x-8">
          <div className="border-r-[0.5px]">
            <div className="flex items-center space-x-2">
              <div className="bg-[#5469D4] min-h-4 min-w-4 rounded-sm" />
              <p>Gross Volume</p>
            </div>
            <div className="flex items-center space-x-4 text-2xl">
              <p className="font-semibold">
                {convertToCurrencyRounded(
                  initialRevenueData.grossRevenue?.requestPeriodValue!
                )}
              </p>
              {
                initialRevenueData.grossRevenue?.percentageChange === 0 ? (
                  <p className="text-sm">No Change from last year</p>
                ) : initialRevenueData.grossRevenue?.percentageChange! > 0 ? (
                  <p className="text-green-500 text-sm">
                    {initialRevenueData.grossRevenue?.percentageChange! > 1000
                      ? "100%+ from last year"
                      : `+${initialRevenueData.grossRevenue?.percentageChange!.toFixed(2)}% from last year`}
                  </p>
                ) : (
                  <p className="text-red-500 text-sm">
                    -{Math.abs(initialRevenueData.grossRevenue?.percentageChange!).toFixed(2)}% from last year
                  </p>
                )
              }
            </div>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <div className="bg-[#E4E4E7]  min-h-4 min-w-4 rounded-sm" />
              <p>Net Volume</p>
            </div>
            <div className="flex items-center space-x-4 text-2xl">
              <p className="font-semibold">
                {convertToCurrencyRounded(
                  initialRevenueData.netRevenue?.requestPeriodValue!
                )}
              </p>
              {
                initialRevenueData.netRevenue?.percentageChange === 0 ? (
                  <p className="text-sm">No Change from last year</p>
                ) : initialRevenueData.netRevenue?.percentageChange! > 0 ? (
                  <p className="text-green-500 text-sm">
                    {initialRevenueData.netRevenue?.percentageChange! > 1000
                      ? "100%+ from last year"
                      : `+${initialRevenueData.netRevenue?.percentageChange!.toFixed(2)}% from last year`}
                  </p>
                ) : (
                  <p className="text-red-500 text-sm">
                    -{Math.abs(initialRevenueData.netRevenue?.percentageChange!).toFixed(2)}% from last year
                  </p>
                )
              }
            </div>
          </div>
        </div>
        <Select
          onValueChange={(e) => {
            handleDateChange(e);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue
              placeholder={`This Year (${dayjs().format("YYYY")})`}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={dayjs().format("YYYY")}>
              This Year ({dayjs().format("YYYY")})
            </SelectItem>
            <SelectItem value={dayjs().subtract(1, "year").format("YYYY")}>
              Last Year ({dayjs().subtract(1, "year").format("YYYY")})
            </SelectItem>
            {[...Array(5).keys()].map((i) => {
              const year = dayjs()
                .subtract(i + 2, "year")
                .format("YYYY");
              return (
                <SelectItem value={year} key={i.toString()}>
                  {year}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-7">
        <div className="col-span-5 border-t-[1px] border-b-[1px] mx-2 my-1">
          <Chart data={initialRevenueData.salesCountByDate} />
        </div>
        <div className="col-span-2 border p-4 rounded-lg flex flex-col space-y-4">
          {initialRevenueData.revenueByPaymentMode?.map(revenue => (<div key={revenue.paymentMode}>
            <p className="text-sm">
              {(
              Object.keys(PaymentMode).find(
                key => PaymentMode[key as keyof typeof PaymentMode] === revenue.paymentMode
              ) || "Unknown"
              )
              .replace(/_/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, char => char.toUpperCase())
              }{" "}Transactions
            </p>
            <div className="flex items-center space-x-4 text-2xl">
              <p className="font-semibold">
                {convertToCurrencyRounded(
                  revenue.comparisionItem?.requestPeriodValue!
                )}
              </p>
              {revenue.comparisionItem?.percentageChange === 0 ? (
                <p className="text-sm">No Change from last year</p>
              ) : revenue.comparisionItem?.percentageChange! > 0 ? (
                <p className="text-green-500 text-sm">
                  {revenue.comparisionItem?.percentageChange! > 1000
                    ? "100%+ from last year"
                    : `+${revenue.comparisionItem?.percentageChange!.toFixed(2)}% from last year`}
                </p>
              ) : (
                <p className="text-red-500 text-sm">
                  -{Math.abs(revenue.comparisionItem?.percentageChange!).toFixed(2)}% from last year
                </p>
              )}
            </div>
          </div>))}
        </div>
      </div>
    </div>
  );
};

export default Revenue;
